// Telecalling/frontend/src/utils/tokenStorage.js
// Cross-domain compatible token storage using cookies

/**
 * Get cookie by name
 */
const getCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(';').shift();
  }
  return null;
};

/**
 * Set cookie with proper domain for cross-subdomain access
 */
const setCookie = (name, value, days = 30) => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  
  // Extract root domain (e.g., tecnvi-ai.com from www.tecnvi-ai.com)
  const hostname = window.location.hostname;
  const domainParts = hostname.split('.');
  
  // Set domain to root domain for cross-subdomain access
  let domain = '';
  if (domainParts.length >= 2 && hostname !== 'localhost') {
    // For tecnvi-ai.com or www.tecnvi-ai.com, use .tecnvi-ai.com
    domain = `;domain=.${domainParts.slice(-2).join('.')}`;
  }
  
  // SameSite=Lax for CSRF protection while allowing navigation
  // Only use Secure flag on HTTPS (production), not on localhost HTTP
  const isSecure = window.location.protocol === 'https:' ? ';Secure' : '';
  document.cookie = `${name}=${value};${expires};path=/;${domain};SameSite=Lax${isSecure}`;
};

/**
 * Delete cookie
 */
const deleteCookie = (name) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const hostname = window.location.hostname;
  const domainParts = hostname.split('.');
  
  let domain = '';
  if (domainParts.length >= 2 && hostname !== 'localhost') {
    domain = `;domain=.${domainParts.slice(-2).join('.')}`;
  }
  
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;${domain}`;
};

/**
 * Migrate tokens from localStorage to cookies (one-time migration)
 */
const migrateTokensFromLocalStorage = () => {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
  const localAccessToken = localStorage.getItem('voicebot_access_token');
  const localRefreshToken = localStorage.getItem('voicebot_refresh_token');
  const localUser = localStorage.getItem('voicebot_user');
  
  if (localAccessToken && !getCookie('voicebot_access_token')) {
    setCookie('voicebot_access_token', localAccessToken);
    console.log('✅ Migrated access token from localStorage to cookie');
  }
  
  if (localRefreshToken && !getCookie('voicebot_refresh_token')) {
    setCookie('voicebot_refresh_token', localRefreshToken);
    console.log('✅ Migrated refresh token from localStorage to cookie');
  }
  
  if (localUser && !getCookie('voicebot_user')) {
    setCookie('voicebot_user', localUser);
    console.log('✅ Migrated user data from localStorage to cookie');
  }
  
  // Optional: Clear localStorage after migration
  // Uncomment these after confirming cookies work in production
  // localStorage.removeItem('voicebot_access_token');
  // localStorage.removeItem('voicebot_refresh_token');
  // localStorage.removeItem('voicebot_user');
};

// Token Storage Interface (drop-in replacement for localStorage methods)
export const tokenStorage = {
  // Migration helper - call this on app initialization
  migrate: migrateTokensFromLocalStorage,
  
  // Get tokens - with fallback to localStorage for backwards compatibility
  getAccessToken: () => {
    return getCookie('voicebot_access_token') || localStorage.getItem('voicebot_access_token');
  },
  getRbacSession: () => {
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
      return null;
    }

    try {
      const rawSession = sessionStorage.getItem('rbac_session');
      if (!rawSession) {
        return null;
      }

      const parsedSession = JSON.parse(rawSession);
      if (!parsedSession?.token) {
        return null;
      }

      if (parsedSession.expiresAt && new Date(parsedSession.expiresAt) < new Date()) {
        return null;
      }

      return parsedSession;
    } catch (error) {
      console.error('Failed to parse RBAC session:', error);
      return null;
    }
  },
  getApiAccessToken: () => {
    const rbacSession = tokenStorage.getRbacSession();
    if (rbacSession?.token) {
      return rbacSession.token;
    }

    return tokenStorage.getAccessToken();
  },
  getRefreshToken: () => {
    return getCookie('voicebot_refresh_token') || localStorage.getItem('voicebot_refresh_token');
  },
  getCurrentUser: () => {
    const cookieUserStr = getCookie('voicebot_user');
    if (cookieUserStr) {
      try {
        return JSON.parse(decodeURIComponent(cookieUserStr));
      } catch (e) {
        console.error('Failed to parse user data from cookie:', e);
      }
    }
    
    // Fallback to localStorage
    const localUserStr = localStorage.getItem('voicebot_user');
    if (localUserStr) {
      try {
        return JSON.parse(localUserStr);
      } catch (e) {
        console.error('Failed to parse user data from localStorage:', e);
      }
    }
    
    return null;
  },
  
  // Set tokens - save to BOTH cookies and localStorage for backwards compatibility
  setAccessToken: (token) => {
    setCookie('voicebot_access_token', token);
    localStorage.setItem('voicebot_access_token', token);
  },
  setRefreshToken: (token) => {
    setCookie('voicebot_refresh_token', token);
    localStorage.setItem('voicebot_refresh_token', token);
  },
  setCurrentUser: (user) => {
    if (user) {
      const userStr = JSON.stringify(user);
      setCookie('voicebot_user', encodeURIComponent(userStr));
      localStorage.setItem('voicebot_user', userStr);
    }
  },
  
  // Set both tokens at once
  setTokens: (accessToken, refreshToken) => {
    setCookie('voicebot_access_token', accessToken);
    setCookie('voicebot_refresh_token', refreshToken);
    localStorage.setItem('voicebot_access_token', accessToken);
    localStorage.setItem('voicebot_refresh_token', refreshToken);
  },
  
  // Clear all auth data - clear BOTH cookies and localStorage
  clearAuth: () => {
    deleteCookie('voicebot_access_token');
    deleteCookie('voicebot_refresh_token');
    deleteCookie('voicebot_user');
    localStorage.removeItem('voicebot_access_token');
    localStorage.removeItem('voicebot_refresh_token');
    localStorage.removeItem('voicebot_user');
    
    // Also clear impersonation markers
    deleteCookie('originalAdminToken');
    deleteCookie('originalAdminUser');
    deleteCookie('token');
    localStorage.removeItem('originalAdminToken');
    localStorage.removeItem('originalAdminUser');
    localStorage.removeItem('token');
  },

  // Clear ONLY impersonation markers
  clearImpersonation: () => {
    deleteCookie('originalAdminToken');
    deleteCookie('originalAdminUser');
    deleteCookie('token');
    localStorage.removeItem('originalAdminToken');
    localStorage.removeItem('originalAdminUser');
    localStorage.removeItem('token');
    console.log('✅ Impersonation markers cleared');
  },
  
  // Check if authenticated
  isAuthenticated: () => {
    return !!getCookie('voicebot_access_token');
  },
  
  // Impersonation support
  setOriginalAdminToken: (token) => setCookie('originalAdminToken', token),
  getOriginalAdminToken: () => getCookie('originalAdminToken'),
  setOriginalAdminUser: (user) => {
    if (user) {
      setCookie('originalAdminUser', encodeURIComponent(JSON.stringify(user)));
    }
  },
  getOriginalAdminUser: () => {
    const userStr = getCookie('originalAdminUser');
    if (userStr) {
      try {
        return JSON.parse(decodeURIComponent(userStr));
      } catch (e) {
        return null;
      }
    }
    return null;
  }
};

// Run migration automatically on import (only once)
if (typeof window !== 'undefined') {
  try {
    migrateTokensFromLocalStorage();
  } catch (e) {
    console.error('Token migration failed:', e);
  }
}
